import { lazyWithPreload } from '@kk/shared/utils/async';
import type { ExtendedRouteObject } from '@/routes/types';
import ExpiringAgreementsHistory from './ExpiringAgreements/ExpiringAgreementsHistory';
import ExpiringAgreementsInfo from './ExpiringAgreements/ExpiringAgreementsInfo';
import loader, { id, type LoaderData, type Params } from './loader';

const ExpiringAgreementsPage = lazyWithPreload(
  async () => await import('./index'),
);
const ExpiringAgreements = lazyWithPreload(
  async () => await import('./ExpiringAgreements/ExpiringAgreements'),
);

const ExpiringFlowLayout = lazyWithPreload(
  async () => await import('./ExpiringAgreements/ExpiringFlowLayout'),
);

const SelectAction = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/contractsExpiring/SelectAction'),
);

const AddInformationToBuy = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/contractsExpiring/AddInformationToBuy'),
);
const AddInformationToSell = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/contractsExpiring/AddInformationToSell'),
);
const AddInformationToExtend = lazyWithPreload(
  async () =>
    await import(
      './ExpiringAgreements/contractsExpiring/AddInformationToExtend'
    ),
);

const ConfirmBuy = lazyWithPreload(
  async () => await import('./ExpiringAgreements/contractsExpiring/ConfirmBuy'),
);
const ConfirmSell = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/contractsExpiring/ConfirmSell'),
);
const ConfirmExtension = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/contractsExpiring/ConfirmExtension'),
);

const AyvensSelectAction = lazyWithPreload(
  async () => await import('./ExpiringAgreements/ayvensCars/SelectAction'),
);

const AyvensAddPickupInfo = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/ayvensCars/AddInformationToPickup'),
);
const AyvensAddExtendInfo = lazyWithPreload(
  async () =>
    await import('./ExpiringAgreements/ayvensCars/AddInformationToExtend'),
);
const AyvensConfirmPickup = lazyWithPreload(
  async () => await import('./ExpiringAgreements/ayvensCars/ConfirmPickup'),
);

export default {
  id,
  path: '/expiringagreements/',
  loader,
  element: <ExpiringAgreementsPage />,
  children: [
    {
      id: 'expiring',
      path: '',
      element: <ExpiringAgreements />,
      children: [
        {
          id: 'expiring-agreements-info-tab',
          index: true,
          element: <ExpiringAgreementsInfo />,
        },
        {
          id: 'expiring-agreements-history-tab',
          path: 'history',
          element: <ExpiringAgreementsHistory />,
        },
      ],
    },

    // Group flow steps in a layout without SideNavigation
    {
      id: 'flow',
      path: 'flow',
      element: <ExpiringFlowLayout />,
      children: [
        { id: 'selectaction', path: 'selectaction', element: <SelectAction /> },
        {
          id: 'addinformationtobuy',
          path: 'addinformationtobuy',
          element: <AddInformationToBuy />,
        },
        {
          id: 'addinformationtosell',
          path: 'addinformationtosell',
          element: <AddInformationToSell />,
        },
        {
          id: 'addinformationtoextend',
          path: 'addinformationtoextend',
          element: <AddInformationToExtend />,
        },
        { id: 'confirmbuy', path: 'confirmbuy', element: <ConfirmBuy /> },
        { id: 'confirmsell', path: 'confirmsell', element: <ConfirmSell /> },
        {
          id: 'confirmextension',
          path: 'confirmextension',
          element: <ConfirmExtension />,
        },

        {
          id: 'ayvensselectaction',
          path: 'ayvensselectaction',
          element: <AyvensSelectAction />,
        },
        {
          id: 'ayvensaddpickupinfo',
          path: 'ayvensaddpickupinfo',
          element: <AyvensAddPickupInfo />,
        },
        {
          id: 'ayvensaddextendinfo',
          path: 'ayvensaddextendinfo',
          element: <AyvensAddExtendInfo />,
        },
        {
          id: 'ayvensconfirmpickup',
          path: 'ayvensconfirmpickup',
          element: <AyvensConfirmPickup />,
        },
      ],
    },
  ],
  handle: ({ params }) => {
    return {
      preload: async () =>
        await Promise.all([
          ExpiringAgreementsPage.preload(),
          loader({ params }),
        ]),
    };
  },
} as const satisfies ExtendedRouteObject<Params, LoaderData>;
